<template>
  <TemplateBase>
    <main class="relative z-0 col-span-full row-start-2">
      <!-- Breadcrumbs -->
      <OrganismNavigationBreadcrumb class="mx-auto p-4 md:container md:mb-16 md:pb-0 md:pt-10 lg:mb-20" :path="breadcrumbs" />

      <div id="pageoverview-page-content" class="mb-8 mx-auto w-full sm:w-8/12">
        <div class="mb-6 mx-2 pb-12 pt-6 bg-white rounded-xl sm:mx-0 md:pt-12">
          <div class="px-4 sm:px-12 lg:px-16">
            <h1 class="mb-3 text-3xl font-semibold uppercase">{{ page.title }}</h1>
            <div class="flex flex-wrap gap-6 items-center">
              <div id="pageoverview-page-search" class="relative w-full max-w-xl">
                <input
                  id="pageoverview-page-searchfield"
                  :value="keyword"
                  type="text"
                  class="pl-4 w-full h-12 bg-app-green-100 border-b-2 border-solid border-app-green-200 rounded-xl"
                  :placeholder="t_('placeholder')"
                  @input="(event) => lazy(() => setKeyword(event.target.value))"
                />
                <AtomButton id="pageoverview-page-btn-search" button-type="secondary-flat-left" class="absolute right-0 top-0">
                  <span class="hidden ml-8 md:inline-block">{{ t_('label') }}</span>
                  <AtomIconSearch class="w-5 h-5 md:ml-8" />
                </AtomButton>
              </div>
              <FormKit
                :value="orderBy"
                class="w-full md:w-auto"
                type="select"
                :options="[
                  { value: 'relevance', label: t_('order-by-relevance') },
                  { value: 'postDate', label: t_('order-by-post-date') },
                ]"
                @input="(value) => setOrderBy(value)"
              />
            </div>
          </div>
          <div class="mb-6 mt-10 border-t-2 border-solid border-app-green-100" />
          <div v-show="keyword" class="mb-6 px-4 text-app-green text-xl font-semibold sm:px-12 lg:px-16">{{ tc_('results', totalHits) }}</div>
          <div v-show="hits.length" class="px-4 sm:px-12 lg:px-16">
            <div v-for="(hit, index) in hits" :key="index" :class="{ 'mb-8': index + 1 < hits.length }" class="group">
              <AtomLink :url="hit.uri" class="group cursor-pointer">
                <h4 class="group-hover:text-app-green font-semibold transition duration-200">{{ hit.title }}</h4>
                <div>
                  <template v-for="(highlight, highlightIndex) in hit.highlights" :key="highlightIndex">
                    <span class="highlights" v-html="highlight + ' '" />
                  </template>
                </div>
                <span class="group-hover:text-app-green underline transition duration-200">{{ t_(hit.sectionHandle) }}</span>
                <span class="px-1">&mdash;</span>
                <span class="italic">{{ d_(hit.postDate, 'short-date') }}</span>
              </AtomLink>
            </div>
          </div>
          <OrganismPagination
            v-show="keyword"
            id="pageoverview-page-paging"
            :count="totalHits"
            :limit="hitsPerPage"
            :page="currentPage"
            class="mb-8 mt-16"
            @click="({ page }) => setCurrentPage(page)"
          />
        </div>
      </div>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import debounce from 'lodash/debounce'
import { storeToRefs } from 'pinia'
import { usePageOverviewStore } from '~/store/page-overview'
import { routerQueryIsEqual } from '~/composables/setup-dynamic-url'
import { setupTranslationPrefixer } from '~/composables/util'

interface Props {
  breadcrumbs: Array<any>
  page: Record<string, any>
}

defineProps<Props>()

const route = useRoute()
const router = useRouter()
const pageOverviewStore = usePageOverviewStore()

const lazy = debounce((call: () => any) => call(), 300)
const { currentPage, getHits: hits, hitsPerPage, keyword, orderBy, totalHits, urlParameters } = storeToRefs(pageOverviewStore)

const { t_, tc_, d_ } = setupTranslationPrefixer('PageOverview')

watch(
  () => urlParameters.value,
  (query) => {
    if (!routerQueryIsEqual(route.query, query)) {
      router.push({ query })
    }
  },
)

const setCurrentPage = (page: number) => pageOverviewStore.setCurrentPage(page)
const setKeyword = (keyword: string) => pageOverviewStore.setKeyword(keyword)
const setOrderBy = (orderBy: string) => pageOverviewStore.setOrderBy(orderBy)
</script>

<style lang="scss">
.highlights em {
  @apply bg-app-lime;
}
</style>
